import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFeaturedEvents, getCategories } from '../lib/api';
import EventCard from '../components/EventCard';
import EventCarousel from '../components/EventCarousel';
import Newsletter from '../components/Newsletter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, ArrowRight, Shield, Zap, CheckCircle, MapPin, Instagram, Facebook, Twitter } from 'lucide-react';

const Home = () => {
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eventsRes, categoriesRes] = await Promise.all([
        getFeaturedEvents(),
        getCategories()
      ]);
      setFeaturedEvents(eventsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/events?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const categoryIcons = {
    // Music subcategories
    bachata: '💃',
    cumbia: '🎶',
    reggaeton: '🎤',
    regional: '🤠',
    tropical: '🎺',
    rock: '🎸',
    vallenato: '🪗',
    pop: '🎵',
    electronica: '🎧',
    'hip-hop': '🎤',
    salsa: '💃',
    merengue: '🥁',
    musical: '🎵',
    // Other categories  
    sports: '🏆',
    arts: '🎨',
    nightlife: '🌙',
    conference: '👥',
    food: '🍽️',
    family: '❤️',
    festival: '🎪',
    other: '⭐'
  };

  // Get all categories for display
  const getAllCategories = () => {
    if (!categories.categories && !categories.music_subcategories) {
      // Old format - array
      return Array.isArray(categories) ? categories : [];
    }
    // New format - object with categories and music_subcategories
    const allCats = [
      ...(categories.music_subcategories || []).map(c => ({ ...c, type: 'musical' })),
      ...(categories.categories || [])
    ];
    return allCats;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section - Optimized for large screens */}
      <section className="relative min-h-[60vh] lg:min-h-[50vh] flex items-center justify-center hero-gradient overflow-hidden py-16 lg:py-12">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-48 h-48 lg:w-64 lg:h-64 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 lg:w-80 lg:h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          {/* Top Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 tag-badge animate-fade-in">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            <span className="text-white/90 text-sm">La plataforma #1 de tickets en Honduras</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-tight text-white animate-slide-up">
            Descubre los mejores
            <br />
            eventos en Honduras
          </h1>
          
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            Compra tickets de forma segura y vive experiencias únicas con 
            validación QR en tiempo real
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto mb-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <div className="search-container">
              <div className="flex-1 flex items-center gap-3 px-4">
                <Search className="w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar eventos, artistas, lugares..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-0 shadow-none focus-visible:ring-0 text-base bg-transparent"
                  data-testid="hero-search-input"
                />
              </div>
              <div className="hidden md:flex items-center gap-3 px-4 border-l border-border">
                <MapPin className="w-5 h-5 text-muted-foreground" />
                <span className="text-muted-foreground">Ciudad</span>
              </div>
              <Button
                type="submit"
                className="btn-primary rounded-xl px-8"
                data-testid="hero-search-button"
              >
                <Search className="w-4 h-4 mr-2 md:hidden" />
                <span>Buscar</span>
              </Button>
            </div>
          </form>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 text-white/70 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <div className="trust-indicator hover:scale-110 transition-transform cursor-default">
              <Shield className="w-5 h-5 text-primary" />
              <span>Pago 100% seguro</span>
            </div>
            <div className="trust-indicator hover:scale-110 transition-transform cursor-default">
              <Zap className="w-5 h-5 text-orange-400" />
              <span>Tickets instantáneos</span>
            </div>
            <div className="trust-indicator hover:scale-110 transition-transform cursor-default">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Validación QR</span>
            </div>
          </div>
        </div>
      </section>

      {/* Event Carousel - Latest 5 Events */}
      {!loading && featuredEvents.length > 0 && (
        <EventCarousel events={featuredEvents} />
      )}

      {/* Categories Section */}
      <section className="py-16 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold mb-8 text-center">Explora por Categoría</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            {getAllCategories().slice(0, 12).map((category, index) => (
              <Link
                key={category.id}
                to={`/events?category=${category.id}`}
                className="category-card animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`category-${category.id}`}
              >
                <div className="text-3xl mb-2 transform group-hover:scale-125 transition-transform">{categoryIcons[category.id] || '⭐'}</div>
                <p className="text-sm font-medium">
                  {category.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Events */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold">Eventos Destacados</h2>
              <p className="text-muted-foreground">Los mejores eventos de la semana</p>
            </div>
            <Button variant="ghost" onClick={() => navigate('/events')} className="group" data-testid="view-all-events">
              Ver todos
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[4/5] rounded-2xl bg-muted animate-pulse" />
              ))}
            </div>
          ) : featuredEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredEvents.slice(0, 6).map((event, index) => (
                <div key={event.event_id} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <EventCard event={event} featured={index === 0} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No hay eventos disponibles</h3>
              <p className="text-muted-foreground">
                Pronto tendremos nuevos eventos para ti.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold mb-2">¿Por qué MasterTicketHN.com?</h2>
            <p className="text-muted-foreground">La plataforma más confiable de Honduras</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="glass-card p-8 text-center hover:scale-105 transition-transform">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">Pago Seguro</h3>
              <p className="text-muted-foreground text-sm">
                Transacciones protegidas con Stripe y PayPal. Tu dinero está seguro.
              </p>
            </div>
            <div className="glass-card p-8 text-center hover:scale-105 transition-transform">
              <div className="w-14 h-14 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-orange-500" />
              </div>
              <h3 className="font-bold text-lg mb-2">Tickets Instantáneos</h3>
              <p className="text-muted-foreground text-sm">
                Recibe tus tickets con código QR inmediatamente después del pago.
              </p>
            </div>
            <div className="glass-card p-8 text-center hover:scale-105 transition-transform">
              <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="font-bold text-lg mb-2">Validación QR</h3>
              <p className="text-muted-foreground text-sm">
                Acceso rápido y seguro con validación de tickets en tiempo real.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-2">No te pierdas ningún evento</h2>
          <p className="text-muted-foreground mb-8">
            Suscríbete y recibe las mejores ofertas y eventos nuevos directamente en tu correo
          </p>
          <Newsletter />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 navy-gradient">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white">
            ¿Organizas Eventos?
          </h2>
          <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
            Únete a MasterTicketHN.com y empieza a vender tickets de tus eventos hoy mismo. 
            Registro gratuito, sin complicaciones.
          </p>
          <Button
            size="lg"
            className="btn-primary text-lg px-10 animate-pulse-glow"
            onClick={() => navigate('/register?role=organizer')}
            data-testid="cta-organizer-button"
          >
            Empezar Gratis
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-bold">MT</span>
                </div>
                <span className="font-bold text-lg">MasterTicketHN.com</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                La plataforma líder de venta de tickets en Honduras.
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold mb-4">Explorar</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/events" className="hover:text-foreground transition-colors">Todos los Eventos</Link></li>
                <li><Link to="/events?category=music" className="hover:text-foreground transition-colors">Conciertos</Link></li>
                <li><Link to="/events?category=sports" className="hover:text-foreground transition-colors">Deportes</Link></li>
                <li><Link to="/events?category=nightlife" className="hover:text-foreground transition-colors">Vida Nocturna</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Organizadores</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/register?role=organizer" className="hover:text-foreground transition-colors">Registrarse</Link></li>
                <li><Link to="/organizer" className="hover:text-foreground transition-colors">Dashboard</Link></li>
                <li><a href="#" className="hover:text-foreground transition-colors">Precios</a></li>
                <li><a href="#" className="hover:text-foreground transition-colors">Soporte</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-foreground transition-colors">Términos de Servicio</a></li>
                <li><a href="#" className="hover:text-foreground transition-colors">Política de Privacidad</a></li>
                <li><a href="#" className="hover:text-foreground transition-colors">Política de Reembolso</a></li>
                <li><a href="#" className="hover:text-foreground transition-colors">Contacto</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © 2024 MasterTicketHN.com - Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-4">
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg" alt="Stripe" className="h-6 opacity-50" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-6 opacity-50" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
